"use client";

import { useState } from "react";

import { Button, ButtonIcon, Icon, Input, View } from "@/components";
import cn from "@/utils/cn";

import type { SearchProps } from "@/components/types";
import type { ChangeEvent, JSX } from "react";

const size = "5x";
/**
 * @function Search
 * @description A real-time search input component with debounce and clear functionality.
 * Filters results as the user types without requiring 'Enter' key.
 * Uses the atomic Input component for consistency.
 *
 * @param {SearchProps} props - Component props.
 * @returns {JSX.Element} The rendered search component.
 *
 * @example
 * ```typescript
 * <Search
 *   onSearch={(val) => setSearchValue(val)}
 *   placeholder="Search users..."
 *   debounce={500}
 * />
 * ```
 */
const InputSearch = ({
  placeholder = "Search",
  className,
  id,
  name,
  onSearch
}: SearchProps): JSX.Element => {
  const [value, setValue] = useState("");

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onSearch(e.target.value);
  };

  /**
   * @description Clears the search input and triggers onSearch with empty string.
   */
  const handleClear = () => {
    setValue("");
    onSearch("");
  };

  return (
    <View className={cn("input-search", className)}>
      <ButtonIcon
        action={() => onSearch(value)}
        iconLeft={{ name: "search", size }}
        className="text-zinc-400 w-5 h-5"
        aria-label="Search"
      />
      <Input
        name={name}
        id={id}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        className="input-search"
      />
      {value && (
        <Button
          onClick={handleClear}
          className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
          type="button"
          aria-label="Clear search"
        >
          <Icon name="x" size={size} />
        </Button>
      )}
    </View>
  );
};

export default InputSearch;
